import Box from "@mui/material/Box";
import Grid from "components/primitives/Grid";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { FieldArray, FieldArrayRenderProps, Form, useFormikContext } from "formik";
import AthleteBulkEditRow from "../AthleteBulkEditRow";
import Mui5 from "components/mui5";
import { Exercise } from "types/exercise";
import usePreferences from "hooks/usePreferences";
import { memo, useCallback, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { FormOneRepMax } from "../hooks/useOneRepMaxFormInitialValues/useOneRepMaxFormInitialValues.types";
import { useTranslation } from "react-i18next";
import Div from "components/primitives/Div";
import Button from "components/Button";

interface AthleteBulkEditFormFieldsProps {
  exercise: Exercise | null;
  spacingX: number;
  onCancel(): void;
  loading: boolean;
}

function AthleteBulkEditFormFields(props: AthleteBulkEditFormFieldsProps) {
  const { spacingX, exercise, loading } = props;
  const { values } = useFormikContext<{ athletesOneRepMaxes: FormOneRepMax[] }>();
  const parentRef = useRef<HTMLDivElement>(null);
  const [{ imperialSystemEnabled }] = usePreferences();
  const { t } = useTranslation();

  const weightInputWidth = 256;
  const weightInputAlignment = exercise?.variants.length ? "center" : "right";

  const rowVirtualizer = useVirtualizer({
    count: values.athletesOneRepMaxes.length,
    getScrollElement: () => parentRef.current,
    estimateSize: useCallback(() => 56, []),
    overscan: 5,
  });

  return (
    <Box
      component={Form}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: (theme) => `calc(100% - ${theme.spacing(9)})`,
      }}
    >
      <Grid
        container
        columns={12}
        py={2}
        px={spacingX}
        color="#FFF"
        sx={{ backgroundColor: "primary.main" }}
        minHeight={(theme) => theme.spacing(6)}
      >
        <Grid size="grow">
          <Typography variant="h4" sx={{ color: "inherit" }}>
            {t("athlete.bulk_edit1rm_table_header_left")}
          </Typography>
        </Grid>
        <Grid width={weightInputWidth} textAlign={weightInputAlignment}>
          <Typography variant="h4" sx={{ color: "inherit" }}>
            {t("athlete.bulk_edit1rm_table_header_right", { unit: imperialSystemEnabled ? "lbs" : "kg" })}
          </Typography>
        </Grid>
      </Grid>
      <Divider />
      <Grid container columns={12} spacing="0px" height={(theme) => `calc(100% - ${theme.spacing(11 + 6)})`}>
        <Grid size={12} pt={1} height="100%">
          <FieldArray
            name="athletesOneRepMaxes"
            render={(arrayHelpers: FieldArrayRenderProps) => (
              <Div ref={parentRef} style={{ overflow: "auto", height: "100%" }}>
                <Div
                  style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    position: "relative",
                    width: "100%",
                  }}
                >
                  {rowVirtualizer.getVirtualItems().map((virtualRow) => (
                    <Div
                      style={{
                        transform: `translateY(${virtualRow.start}px)`,
                        height: `${virtualRow.size}px`,
                        top: 0,
                        left: 0,
                        position: "absolute",
                        width: "100%",
                      }}
                      key={`${values.athletesOneRepMaxes[virtualRow.index].athleteId}-${
                        values.athletesOneRepMaxes[virtualRow.index].exerciseId
                      }`}
                    >
                      <AthleteBulkEditRow
                        athlete1rmValue={values.athletesOneRepMaxes[virtualRow.index]}
                        athlete1rmValueIndex={virtualRow.index}
                        arrayHelpers={arrayHelpers}
                        spacingX={spacingX}
                      />
                    </Div>
                  ))}
                </Div>
              </Div>
            )}
          />
        </Grid>
      </Grid>
      <Divider />
      <Grid container alignItems="center" px={spacingX} py={1} minHeight={(theme) => theme.spacing(11)}>
        <Grid size="grow">
          <Mui5.Button fullWidth onClick={props.onCancel} variant="outlined">
            {t("global.cancel_button_text")}
          </Mui5.Button>
        </Grid>
        <Grid>
          <Box
            sx={{
              px: 1.5,
            }}
          />
        </Grid>
        <Grid size="grow">
          <Button fullWidth type="submit" loading={loading} disabled={loading}>
            {t("global.submit_button_text")}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

export default memo(AthleteBulkEditFormFields);
